// ============================================================================
// HAI Reporting Tool - Admin Service
// Administrative functions for managing facilities, wards, and users
// ============================================================================

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  where
} from 'firebase/firestore';
import { db } from './firebase';
import { db as localDb } from './db';
import type { Facility, Ward, User, UserRole } from '@/types';

// ============================================================================
// Facility Management
// ============================================================================

export async function createFacility(data: {
  name: string;
  region: string;
}): Promise<string> {
  try {
    const id = crypto.randomUUID();
    const docRef = doc(db, 'facilities', id);

    const facilityData = {
      name: data.name,
      region: data.region,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(docRef, facilityData);

    return id;
  } catch (error) {
    console.error('Error creating facility:', error);
    throw error;
  }
}

export async function updateFacility(
  facilityId: string,
  data: {
    name?: string;
    region?: string;
    isActive?: boolean;
  }
): Promise<void> {
  try {
    const docRef = doc(db, 'facilities', facilityId);

    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating facility:', error);
    throw error;
  }
}

export async function getAllFacilities(): Promise<Facility[]> {
  try {
    const q = query(collection(db, 'facilities'), orderBy('name'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      name: doc.data().name,
      region: doc.data().region,
      isActive: doc.data().isActive,
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    }));
  } catch (error) {
    console.error('Error fetching facilities:', error);
    throw error;
  }
}

export async function deleteFacility(facilityId: string): Promise<void> {
  try {
    // Check if facility has wards
    const wardsQuery = query(
      collection(db, 'wards'),
      where('facilityId', '==', facilityId)
    );
    const wardsSnapshot = await getDocs(wardsQuery);

    if (!wardsSnapshot.empty) {
      throw new Error('Cannot delete facility with existing wards. Please delete or reassign wards first.');
    }

    await deleteDoc(doc(db, 'facilities', facilityId));
  } catch (error) {
    console.error('Error deleting facility:', error);
    throw error;
  }
}

// ============================================================================
// Ward Management
// ============================================================================

export async function createWard(data: {
  facilityId: string;
  name: string;
  description: string;
}): Promise<string> {
  try {
    const id = crypto.randomUUID();
    const docRef = doc(db, 'wards', id);

    const wardData = {
      facilityId: data.facilityId,
      name: data.name,
      description: data.description,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(docRef, wardData);

    return id;
  } catch (error) {
    console.error('Error creating ward:', error);
    throw error;
  }
}

export async function updateWard(
  wardId: string,
  data: {
    name?: string;
    description?: string;
    isActive?: boolean;
    facilityId?: string;
  }
): Promise<void> {
  try {
    const docRef = doc(db, 'wards', wardId);

    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating ward:', error);
    throw error;
  }
}

export async function getAllWards(): Promise<Ward[]> {
  try {
    const q = query(collection(db, 'wards'), orderBy('name'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      facilityId: doc.data().facilityId,
      name: doc.data().name,
      description: doc.data().description || '',
      isActive: doc.data().isActive,
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    }));
  } catch (error) {
    console.error('Error fetching wards:', error);
    throw error;
  }
}

export async function getWardsByFacility(facilityId: string): Promise<Ward[]> {
  try {
    const q = query(
      collection(db, 'wards'),
      where('facilityId', '==', facilityId),
      orderBy('name')
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      facilityId: doc.data().facilityId,
      name: doc.data().name,
      description: doc.data().description || '',
      isActive: doc.data().isActive,
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    }));
  } catch (error) {
    console.error('Error fetching wards by facility:', error);
    throw error;
  }
}

export async function deleteWard(wardId: string): Promise<void> {
  try {
    // Check if ward has data
    const deviceSnapshotsQuery = query(
      collection(db, 'deviceSnapshots'),
      where('wardId', '==', wardId)
    );
    const deviceSnapshotsSnapshot = await getDocs(deviceSnapshotsQuery);

    if (!deviceSnapshotsSnapshot.empty) {
      throw new Error('Cannot delete ward with existing data. Please deactivate instead.');
    }

    await deleteDoc(doc(db, 'wards', wardId));
  } catch (error) {
    console.error('Error deleting ward:', error);
    throw error;
  }
}

// ============================================================================
// User Management
// ============================================================================

export async function getAllUsers(): Promise<User[]> {
  try {
    const q = query(collection(db, 'users'), orderBy('displayName'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      displayName: doc.data().displayName,
      email: doc.data().email,
      role: doc.data().role as UserRole,
      facilityId: doc.data().facilityId || '',
      isActive: doc.data().isActive,
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      lastLoginAt: doc.data().lastLoginAt?.toDate() || new Date()
    }));
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
}

export async function updateUserRole(
  userId: string,
  role: UserRole
): Promise<void> {
  try {
    const docRef = doc(db, 'users', userId);

    await updateDoc(docRef, {
      role,
      updatedAt: serverTimestamp()
    });

    // Update local cache
    const userDoc = await getDoc(docRef);
    if (userDoc.exists()) {
      const user: User = {
        id: userDoc.id,
        displayName: userDoc.data().displayName,
        email: userDoc.data().email,
        role: userDoc.data().role as UserRole,
        facilityId: userDoc.data().facilityId || '',
        isActive: userDoc.data().isActive,
        createdAt: userDoc.data().createdAt?.toDate() || new Date(),
        updatedAt: userDoc.data().updatedAt?.toDate() || new Date(),
        lastLoginAt: userDoc.data().lastLoginAt?.toDate() || new Date()
      };
      await localDb.users.put(user);
    }
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
}

export async function updateUserFacility(
  userId: string,
  facilityId: string
): Promise<void> {
  try {
    const docRef = doc(db, 'users', userId);

    console.log(`[updateUserFacility] Starting update for user ${userId}`);
    console.log(`[updateUserFacility] Facility to assign:`, facilityId);

    await updateDoc(docRef, {
      facilityId,
      updatedAt: serverTimestamp()
    });

    console.log(`[updateUserFacility] Firestore update completed for user ${userId}`);

    // Update local cache
    const userDoc = await getDoc(docRef);
    if (userDoc.exists()) {
      const user: User = {
        id: userDoc.id,
        displayName: userDoc.data().displayName,
        email: userDoc.data().email,
        role: userDoc.data().role as UserRole,
        facilityId: userDoc.data().facilityId || '',
        isActive: userDoc.data().isActive,
        createdAt: userDoc.data().createdAt?.toDate() || new Date(),
        updatedAt: userDoc.data().updatedAt?.toDate() || new Date(),
        lastLoginAt: userDoc.data().lastLoginAt?.toDate() || new Date()
      };
      console.log(`[updateUserFacility] Local cache updated. Facility ID in cache:`, user.facilityId);
      await localDb.users.put(user);
    }
  } catch (error) {
    console.error('Error updating user facility:', error);
    throw error;
  }
}

export async function toggleUserStatus(
  userId: string,
  isActive: boolean
): Promise<void> {
  try {
    const docRef = doc(db, 'users', userId);

    await updateDoc(docRef, {
      isActive,
      updatedAt: serverTimestamp()
    });

    // Update local cache
    const userDoc = await getDoc(docRef);
    if (userDoc.exists()) {
      const user: User = {
        id: userDoc.id,
        displayName: userDoc.data().displayName,
        email: userDoc.data().email,
        role: userDoc.data().role as UserRole,
        facilityId: userDoc.data().facilityId || '',
        isActive: userDoc.data().isActive,
        createdAt: userDoc.data().createdAt?.toDate() || new Date(),
        updatedAt: userDoc.data().updatedAt?.toDate() || new Date(),
        lastLoginAt: userDoc.data().lastLoginAt?.toDate() || new Date()
      };
      await localDb.users.put(user);
    }
  } catch (error) {
    console.error('Error toggling user status:', error);
    throw error;
  }
}
